import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addNewStructurePosition,
  getCurrentOrganizationalStructure,
  getCurrentOrganizationalStructureAction,
  updateOrganizationalStructurePage
} from '../../../shared/services/organizational-structure-service';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretLeft, faCaretRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import StructurePositionForm from '../components/forms/structure-position-form';
import { notificationService } from '../../../shared/services/notification-service';
import { getMessageContents } from '../../../shared/utils/error-messages';
import { convertToCurrency } from '../../../shared/utils/currency';
import SaveDraft from '../functions/save-draft';
import ConfirmModal from '../../../shared/components/molecules/modals/confirm-modal';

const StructurePositionPage: FC = () => {
  const dispatch = useDispatch();
  const organizationalStructure = useSelector(getCurrentOrganizationalStructure);
  let {id} = useParams();
  const organizationalStructureAction = useSelector(getCurrentOrganizationalStructureAction);
  const disable = organizationalStructureAction.status === 'view';
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showSkipModalData, setShowSkipModalData] = useState<any>({
    title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
    message: 'ต้องการข้ามกรอกข้อมูลส่วนนี้หรือไม่',
  })

  useEffect(() => {
    updateOrganizationalStructurePage('structure-position', dispatch);
  }, []);

  const getPreviousLink = () => {
    if (id) {
      return `/organizational-structures/${id}/structure-department`;
    }
    return '/organizational-structures/create/structure-department';
  };

  const getNextLink = () => {
    if (id) {
      navigate(`/organizational-structures/${id}/personnel`);
    } else {
      navigate('/organizational-structures/create/personnel');
    }
  };

  const addNewData = () => {
    addNewStructurePosition(
      {
        value: {
          id: null,
          structure_department_id: null,
          structure_department_index: null,
          pay_account_number: null,
          name: null,
          power_rate: null,
          level: '',
        }
      },
      dispatch
    );
  };

  const DisplayButton = () => {
    if (!disable) {
      return <button className="btn btn-primary d-block px-4 btn-action-height btn-mof-primary" onClick={addNewData}
                     hidden={disable} type="button">
        <FontAwesomeIcon icon={faPlus} className="me-2"/>
        เพิ่มข้อมูล
      </button>;
    }
    return;
  };

  const validateForm = () => {
    if (organizationalStructureAction.status === 'view') {
      getNextLink();
      return;
    }
    setSubmitted(true);
    const inValid = organizationalStructure.structure_positions
      .some(data => {
        return !data.name ||
          !data.pay_account_number ||
          !data.level ||
          typeof data.structure_department_index !== 'number' ||
          (data.power_rate || -1) < 0;
      });
    if (inValid) {
      setShowSkipModalData({
        title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
        message: 'ต้องการข้ามกรอกข้อมูลส่วนนี้หรือไม่',
      })
      setShowSkipModal(true);
      return;
    }
    const powerRateCap = organizationalStructure.structure_departments.map((structuredDepartmentData, index) => {
      const powerRateUse = organizationalStructure.structure_positions
        .filter(data => data.structure_department_index === index)
        .map(data => data.power_rate || 0)
        .map(Number)
        .reduce((total, power_rate) => {
          return total + power_rate;
        }, 0);
      return {
        name: `${structuredDepartmentData.name}`,
        power_rate: !structuredDepartmentData.is_empty ? ((structuredDepartmentData.power_rate || 0) - 1) - powerRateUse : (structuredDepartmentData.power_rate || 0) - powerRateUse
      };
    });
    const powerRateRemaining = powerRateCap.filter(data => data.power_rate > 0);
    const powerRateOverLimit = powerRateCap.filter(data => data.power_rate < 0);

    if (powerRateRemaining.length > 0) {
      const message = powerRateRemaining.map(data => `${data.name} ยังเหลืออัตรากำลังอีก ${convertToCurrency(data.power_rate)} คน`);

      // notificationService.warningNotification('อัตรากำลังยังไม่ครบกำหนด', getMessageContents(message), 6000);
      setShowSkipModalData({
        title: 'อัตรากำลังยังไม่ครบกำหนด',
        message: getMessageContents(message),
      })
      setShowSkipModal(true);
      return;
    } else if (powerRateOverLimit.length > 0) {
      const message = powerRateOverLimit.map(data => `${data.name} อัตรากำลังเกิน ${convertToCurrency(Math.abs(data.power_rate))} คน`);

      // notificationService.warningNotification('อัตรากำลังเกินกำหนด', getMessageContents(message), 6000);
      setShowSkipModalData({
        title: 'อัตรากำลังเกินกำหนด',
        message: getMessageContents(message),
      })
      setShowSkipModal(true);
      return;
    }
    getNextLink();
  };

  const handleCloseSkipModal = () => {
    setShowSkipModal(false);
  }

  const handleConfirmSkipModal = () => {
    getNextLink();
  }

  return (
    <Form noValidate validated={organizationalStructureAction.isSubmitted || submitted}
          className="custom-no-validation">
      <ConfirmModal
        show={showSkipModal}
        title={showSkipModalData.title}
        description={showSkipModalData.message}
        confirmLabel={'ใช่'}
        onHide={handleCloseSkipModal}
        onConfirm={handleConfirmSkipModal}
      />
      <div className="h-100 content-background-color rounded-4 px-4 py-4">
        {
          organizationalStructure.structure_positions.map((data, index) => {
            return <StructurePositionForm key={index}
                                          index={index}
                                          lastButton={index === organizationalStructure.structure_positions.length - 1}
                                          {...data}/>;
          })
        }
        <div className="row">
          <div className="col-12">
            {DisplayButton()}
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end my-4">
        <Link to={getPreviousLink()}>
          <Button variant="secondary" type="button" className="btn-action me-2">
            <FontAwesomeIcon icon={faCaretLeft} className="me-2"/>
            ย้อนกลับ
          </Button>
        </Link>
        {organizationalStructure?.status === "DRAFT" && <SaveDraft />}
        <Button variant="primary" type="button" className="btn-action me-2" onClick={validateForm}>
          ถัดไป
          <FontAwesomeIcon icon={faCaretRight} className="ms-2"/>
        </Button>
      </div>
    </Form>
  );
};

export default StructurePositionPage;